import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { CheckCircle2, ChevronLeft, Circle, Search, UserPlus, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

interface NewStarter {
  id: string;
  name: string;
  email: string;
  startDate: string;
  department: string;
  role: string;
  onboardingProgress: {
    documentsSigned: boolean;
    itAccountSetup: boolean;
    uniformIssued: boolean;
    trainingScheduled: boolean;
    equipmentProvided: boolean;
    inductionCompleted: boolean;
  };
}

const MOCK_NEW_STARTERS: NewStarter[] = [
  {
    id: '1',
    name: 'John Anderson',
    email: 'john.anderson@company.com',
    startDate: '2026-03-25',
    department: 'Field Services',
    role: 'Engineer',
    onboardingProgress: {
      documentsSigned: true,
      itAccountSetup: true,
      uniformIssued: true,
      trainingScheduled: true,
      equipmentProvided: false,
      inductionCompleted: false,
    },
  },
  {
    id: '2',
    name: 'Sarah Williams',
    email: 'sarah.williams@company.com',
    startDate: '2026-03-27',
    department: 'Operations',
    role: 'Technician',
    onboardingProgress: {
      documentsSigned: true,
      itAccountSetup: true,
      uniformIssued: false,
      trainingScheduled: false,
      equipmentProvided: false,
      inductionCompleted: false,
    },
  },
  {
    id: '3',
    name: 'Michael Brown',
    email: 'michael.brown@company.com',
    startDate: '2026-04-01',
    department: 'Wastewater Treatment',
    role: 'Supervisor',
    onboardingProgress: {
      documentsSigned: true,
      itAccountSetup: false,
      uniformIssued: false,
      trainingScheduled: false,
      equipmentProvided: false,
      inductionCompleted: false,
    },
  },
  {
    id: '4',
    name: 'Emily Davis',
    email: 'emily.davis@company.com',
    startDate: '2026-04-05',
    department: 'Quality Control',
    role: 'Engineer',
    onboardingProgress: {
      documentsSigned: false,
      itAccountSetup: false,
      uniformIssued: false,
      trainingScheduled: false,
      equipmentProvided: false,
      inductionCompleted: false,
    },
  },
  {
    id: '5',
    name: 'James Wilson',
    email: 'james.wilson@company.com',
    startDate: '2026-04-10',
    department: 'Emergency Response',
    role: 'Operator',
    onboardingProgress: {
      documentsSigned: false,
      itAccountSetup: false,
      uniformIssued: false,
      trainingScheduled: false,
      equipmentProvided: false,
      inductionCompleted: false,
    },
  },
];

const ONBOARDING_STEPS = [
  { key: 'documentsSigned' as const, label: 'Documents Signed' },
  { key: 'itAccountSetup' as const, label: 'IT Account Setup' },
  { key: 'uniformIssued' as const, label: 'Uniform Issued' },
  { key: 'trainingScheduled' as const, label: 'Training Scheduled' },
  { key: 'equipmentProvided' as const, label: 'Equipment Provided' },
  { key: 'inductionCompleted' as const, label: 'Induction Completed' },
];

export default function NewStartersPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('All Departments');
  const [statusFilter, setStatusFilter] = useState('All Statuses');

  const calculateProgress = (progress: NewStarter['onboardingProgress']) => {
    const completed = Object.values(progress).filter(Boolean).length;
    const total = Object.values(progress).length;
    return Math.round((completed / total) * 100);
  };

  const filteredStarters = MOCK_NEW_STARTERS.filter((starter) => {
    const matchesSearch =
      searchQuery === '' ||
      starter.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      starter.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      starter.role.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesDepartment =
      departmentFilter === 'All Departments' || starter.department === departmentFilter;

    const progress = calculateProgress(starter.onboardingProgress);
    const matchesStatus =
      statusFilter === 'All Statuses' ||
      (statusFilter === 'Complete' && progress === 100) ||
      (statusFilter === 'In Progress' && progress > 0 && progress < 100) ||
      (statusFilter === 'Not Started' && progress === 0);

    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const getStatusBadge = (progress: number) => {
    if (progress === 100) {
      return 'bg-green-100 text-green-800 border-green-300';
    }
    if (progress > 0) {
      return 'bg-blue-100 text-blue-800 border-blue-300';
    }
    return 'bg-slate-100 text-slate-800 border-slate-300';
  };

  const getStatusLabel = (progress: number) => {
    if (progress === 100) return 'Complete';
    if (progress > 0) return 'In Progress';
    return 'Not Started';
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="New Starters"
        subtitle={`${filteredStarters.length} upcoming new starters tracked`}
        icon={<UserPlus className="w-7 h-7" style={{ color: '#E84A2B' }} />}
        breadcrumbs={[
          { label: 'Home', href: '/', current: false },
          { label: 'Users', href: '/users', current: false },
          { label: 'New Starters', current: true },
        ]}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate('/users')}
            className="border-border hover:bg-accent rounded-lg"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Users
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto px-6 space-y-4">
        {/* Filters */}
        <div className="bg-card border border-border rounded-lg shadow-sm p-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
              <Input
                placeholder="Search new starters..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 bg-background border-border"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-10"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[200px] rounded-lg border-border">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All Departments">All Departments</SelectItem>
                <SelectItem value="Field Services">Field Services</SelectItem>
                <SelectItem value="Operations">Operations</SelectItem>
                <SelectItem value="Wastewater Treatment">Wastewater Treatment</SelectItem>
                <SelectItem value="Quality Control">Quality Control</SelectItem>
                <SelectItem value="Emergency Response">Emergency Response</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] rounded-lg border-border">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All Statuses">All Statuses</SelectItem>
                <SelectItem value="Complete">Complete</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Not Started">Not Started</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* New Starters Table */}
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="sticky top-0 bg-card/90 backdrop-blur-sm z-10">
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Name</TableHead>
                <TableHead className="text-muted-foreground">Start Date</TableHead>
                <TableHead className="text-muted-foreground">Department / Role</TableHead>
                <TableHead className="text-muted-foreground">Onboarding Progress</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStarters.map((starter) => {
                const progress = calculateProgress(starter.onboardingProgress);
                return (
                  <TableRow key={starter.id} className="border-border hover:bg-accent/50">
                    <TableCell>
                      <div>
                        <div className="font-semibold text-foreground">{starter.name}</div>
                        <div className="text-xs text-muted-foreground">{starter.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-foreground">
                        {new Date(starter.startDate).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm text-foreground">{starter.department}</div>
                        <div className="text-xs text-muted-foreground">{starter.role}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-4">
                          <Badge
                            variant="outline"
                            className={`text-xs ${getStatusBadge(progress)}`}
                          >
                            {getStatusLabel(progress)}
                          </Badge>
                          <span className="text-xs font-semibold text-foreground">{progress}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div
                            className="h-full transition-all rounded-full bg-[#E84A2B]"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-1 pt-1">
                          {ONBOARDING_STEPS.map((step) => (
                            <div
                              key={step.key}
                              className="flex items-center gap-1 text-xs"
                              title={step.label}
                            >
                              {starter.onboardingProgress[step.key] ? (
                                <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                              ) : (
                                <Circle className="w-3 h-3 text-slate-300 flex-shrink-0" />
                              )}
                              <span className="text-muted-foreground truncate">{step.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {filteredStarters.length === 0 && (
            <div className="flex items-center justify-center h-[200px] text-muted-foreground">
              <div className="text-center">
                <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No new starters found matching your filters</p>
              </div>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">Total New Starters</div>
            <div className="text-2xl font-bold text-foreground">{MOCK_NEW_STARTERS.length}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">Onboarding Complete</div>
            <div className="text-2xl font-bold text-green-600">
              {
                MOCK_NEW_STARTERS.filter((s) => calculateProgress(s.onboardingProgress) === 100)
                  .length
              }
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">In Progress</div>
            <div className="text-2xl font-bold text-blue-600">
              {
                MOCK_NEW_STARTERS.filter((s) => {
                  const progress = calculateProgress(s.onboardingProgress);
                  return progress > 0 && progress < 100;
                }).length
              }
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">Not Started</div>
            <div className="text-2xl font-bold text-slate-600">
              {
                MOCK_NEW_STARTERS.filter((s) => calculateProgress(s.onboardingProgress) === 0)
                  .length
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
